import React, { useState } from "react";
import {
  Space,
  message,
  Table,
  Button,
  Typography,
  Upload,
  Popconfirm,
  DatePicker,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Util from "../../Util";
import { PAYMENT_STATUS, downloadAsCsv } from "./CommonUtils";

const { Title, Paragraph } = Typography;

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
const MAX_DP_CELLS = 20000000;

// หาชุดรายการรอจ่าย (paymentDetail ของ vendor) ที่ยอดจ่าย 60% รวมกันเท่ากับยอดใน CSV
// คืน { rows } เมื่อมีชุดเดียวที่ตรง, { ambiguous: true } เมื่อมีหลายชุด, null เมื่อไม่มี
export const findPaidRows = (rows, csvAmount) => {
  const units = rows.map((r) => Math.round(r.rawTotal * 100));
  const g = units.reduce((a, b) => gcd(a, b), 0) || 1;
  const items = units.map((u) => u / g);
  const maxSum = items.reduce((a, b) => a + b, 0);
  if (items.length * (maxSum + 1) > MAX_DP_CELLS) return null;

  // layers[i][s] = จำนวนชุดจาก items[0..i) ที่รวมได้ s (สูงสุด 2)
  const layers = [new Uint8Array(maxSum + 1)];
  layers[0][0] = 1;
  items.forEach((w, i) => {
    const prev = layers[i];
    const cur = prev.slice();
    for (let s = w; s <= maxSum; s++)
      if (prev[s - w]) cur[s] = Math.min(2, cur[s] + prev[s - w]);
    layers.push(cur);
  });

  const center = Math.round((parseFloat(csvAmount) * 100) / 0.6 / g);
  let target = -1;
  let count = 0;
  for (let s = Math.max(1, center - 2); s <= Math.min(maxSum, center + 2); s++) {
    if (!layers[items.length][s]) continue;
    if (((s * g * 0.6) / 100).toFixed(2) !== csvAmount) continue;
    count += layers[items.length][s];
    target = s;
  }
  if (count === 0) return null;
  if (count > 1) return { ambiguous: true };

  const picked = [];
  let s = target;
  for (let i = items.length; i > 0; i--) {
    if (layers[i - 1][s]) continue; // ชุดเดียวที่ตรงไม่ได้ใช้รายการนี้
    picked.push(rows[i - 1]);
    s -= items[i - 1];
  }
  return { rows: picked.reverse() };
};

function PaymentUpdate() {
  const STAGE = {
    UPLOAD: 0,
    CHECK: 1,
  };

  const [messageApi, contextHolder] = message.useMessage();
  const [stage, setStage] = useState(STAGE.UPLOAD);
  const [loading, setLoading] = useState(false);
  const [matchingPayments, setMatchingPayments] = useState([]);
  const [mismatchedPayments, setMismatchedPayments] = useState([]);
  const [pendingWithoutMatch, setPendingWithoutMatch] = useState([]);
  const [csvWithoutMatch, setCsvWithoutMatch] = useState([]);
  const [partialPayments, setPartialPayments] = useState([]);
  // รายได้เดือนหลังจากนี้เป็นของรอบถัดไป ไม่นำมาเทียบกับ CSV
  const [lastIncomeMonth, setLastIncomeMonth] = useState(null);

  const loadPendingData = async () => {
    const data = await Util.sendGet("/api/moresheetStaff/getHytextsTopay", {});
    if (!data.success) {
      messageApi.error("มีข้อผิดพลาด");
      return;
    }
    const lastYear = lastIncomeMonth.year();
    const lastMonth = lastIncomeMonth.month() + 1;
    const payments = data.data.payments
      .filter(
        (row) =>
          row.year < lastYear ||
          (row.year === lastYear && row.month <= lastMonth)
      )
      .sort((a, b) => {
        if (a.year < b.year) return -1;
        if (a.year > b.year) return 1;
        if (a.month === b.month) return 0;
        return a.month < b.month ? -1 : 1;
      });
    const relaventSellers = data.data.relaventSellers;

    // Group by facebookID
    const facebookIDGroup = {}; // 100% revenue, need to calculate 60%
    for (const row of payments) {
      if (!facebookIDGroup.hasOwnProperty(row.author)) {
        facebookIDGroup[row.author] = {
          total: 0,
          paymentIds: [],
          paymentDetail: [],
        };
      }
      facebookIDGroup[row.author].total += row.total;
      facebookIDGroup[row.author].paymentIds.push(row._id);
      facebookIDGroup[row.author].paymentDetail.push({
        _id: row._id,
        rawTotal: row.total,
        month: `${row.year}-${row.month}`,
        total: row.total.toFixed(2),
        payout: (row.total * 0.6).toFixed(2),
      });
    }

    const htiVendorMap = new Map();
    const vendorIdGroup = {};
    for (const seller of relaventSellers) {
      if (
        !facebookIDGroup.hasOwnProperty(seller.facebookID) ||
        !seller.htiVendorId
      )
        continue;
      const { total, paymentIds, paymentDetail } =
        facebookIDGroup[seller.facebookID];
      const splitBank = seller.bankInfo.split(",");
      const payBank = splitBank.length === 2 ? splitBank[0] : "Invalid";
      if (!vendorIdGroup.hasOwnProperty(seller.htiVendorId)) {
        vendorIdGroup[seller.htiVendorId] = {
          total: 0,
          paymentIds: [],
          paymentDetail: [],
          htiVendorName: seller.htiVendorName,
          peopleID: seller.peopleID,
          realName: seller.realName,
          payBank,
        };
      }
      vendorIdGroup[seller.htiVendorId].total += total;
      vendorIdGroup[seller.htiVendorId].paymentIds.push(...paymentIds);
      vendorIdGroup[seller.htiVendorId].paymentDetail.push(...paymentDetail);
    }

    Object.entries(vendorIdGroup).forEach(([key, val]) => {
      const payout = val.total * 0.6;
      htiVendorMap.set(key, {
        ...val,
        payout: payout.toFixed(2),
      });
    });

    return htiVendorMap;
  };

  const fileChanged = async (file) => {
    setLoading(true);
    try {
      const pendingHytextsVendorIdMap = await loadPendingData();
      const filedata = await Util.fileReaderAsync(file);
      const filelines = filedata.split("\n");
      const usedVendorId = [];
      const matching = [];
      const mismatched = [];
      const partial = [];
      const csvNoMatch = [];

      for (let i = 1; i < filelines.length; i++) {
        const line = filelines[i].trim().split(",");
        if (line.length < 3) continue;
        const vendorId = line[0];
        const vendorName = line[1];
        const amount = parseFloat(line[2])?.toFixed(2);

        const mapData = pendingHytextsVendorIdMap.get(vendorId);
        if (!mapData) {
          csvNoMatch.push({ vendorId, vendorName, amount });
          continue;
        }
        usedVendorId.push(vendorId);
        const pendingAmount = mapData.payout;
        if (pendingAmount !== amount) {
          // ยอดรอจ่ายอาจมีรายการค้างเก่าปนอยู่ ลองหาชุดรายการที่ตรงกับยอดที่โอนจริง
          const found = findPaidRows(mapData.paymentDetail, amount);
          if (found && found.rows) {
            const paidIdSet = new Set(found.rows.map((r) => r._id));
            const remaining = mapData.paymentDetail
              .filter((r) => !paidIdSet.has(r._id))
              .reduce((sum, r) => sum + r.rawTotal * 0.6, 0);
            partial.push({
              ...mapData,
              htiVendorId: vendorId,
              csvAmount: amount,
              paidIds: [...paidIdSet],
              paidMonths: found.rows.map((r) => r.month).join(" "),
              remainingPayout: remaining.toFixed(2),
            });
            continue;
          }
          mismatched.push({
            ...mapData,
            htiVendorId: vendorId,
            csvAmount: amount,
            note: found && found.ambiguous ? "มีหลายชุดที่ยอดตรง" : "",
          });
          continue;
        }

        matching.push({
          ...mapData,
          htiVendorId: vendorId,
        });
      }

      const pendingNoMatch = [];
      for (const [key, val] of pendingHytextsVendorIdMap.entries()) {
        if (!usedVendorId.includes(key)) {
          pendingNoMatch.push({
            ...val,
            htiVendorId: key,
          });
        }
      }

      setMatchingPayments(matching);
      setMismatchedPayments(mismatched);
      setPartialPayments(partial);
      setPendingWithoutMatch(pendingNoMatch);
      setCsvWithoutMatch(csvNoMatch);
      setStage(STAGE.CHECK);
    } catch (err) {
      messageApi.error("ไฟล์ไม่ถูกต้อง");
    }
    setLoading(false);
  };

  const confirmPaymentUpdate = async () => {
    setLoading(true);
    const idsToUpdate = matchingPayments.flatMap((p) => p.paymentIds);
    if (idsToUpdate.length === 0) {
      messageApi.warning("ไม่มีรายการที่ตรงกันให้อัพเดต");
      setLoading(false);
      return;
    }

    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: idsToUpdate,
        paymentStatus: PAYMENT_STATUS.SUCCESS,
        paymentPausedReason: "",
      }
    );

    if (result.success) {
      messageApi.success(`อัพเดตสถานะสำเร็จ ${result.modifiedCount} รายการ`);
    } else {
      messageApi.error(`อัพเดตสถานะไม่สำเร็จ: ${result.message}`);
    }
    setLoading(false);
    setStage(STAGE.UPLOAD);
  };

  const confirmPartialUpdate = async () => {
    setLoading(true);
    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: partialPayments.flatMap((p) => p.paidIds),
        paymentStatus: PAYMENT_STATUS.SUCCESS,
        paymentPausedReason: "",
      }
    );

    if (result.success) {
      messageApi.success(`อัพเดตสถานะสำเร็จ ${result.modifiedCount} รายการ`);
      setPartialPayments([]);
    } else {
      messageApi.error(`อัพเดตสถานะไม่สำเร็จ: ${result.message}`);
    }
    setLoading(false);
  };

  const confirmMismatchedUpdate = async () => {
    setLoading(true);
    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: mismatchedPayments.flatMap((p) => p.paymentIds),
        paymentStatus: PAYMENT_STATUS.PAUSED,
        paymentPausedReason: "การจ่ายไม่สำเร็จ",
      }
    );

    if (result.success) {
      messageApi.success(`อัพเดตสถานะสำเร็จ ${result.modifiedCount} รายการ`);
      setMismatchedPayments([]);
    } else {
      messageApi.error(`อัพเดตสถานะไม่สำเร็จ: ${result.message}`);
    }
    setLoading(false);
  };

  const confirmPendingNoMatchUpdate = async () => {
    setLoading(true);
    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: pendingWithoutMatch.flatMap((p) => p.paymentIds),
        paymentStatus: PAYMENT_STATUS.PAUSED,
        paymentPausedReason: "การจ่ายไม่สำเร็จ",
      }
    );

    if (result.success) {
      messageApi.success(`อัพเดตสถานะสำเร็จ ${result.modifiedCount} รายการ`);
      setPendingWithoutMatch([]);
    } else {
      messageApi.error(`อัพเดตสถานะไม่สำเร็จ: ${result.message}`);
    }
    setLoading(false);
  };

  const CheckStage = () => {
    const baseColumns = [
      { title: "Vendor ID", dataIndex: "htiVendorId", key: "htiVendorId" },
      {
        title: "Vendor Name",
        dataIndex: "htiVendorName",
        key: "htiVendorName",
      },
      { title: "เลขบัตรประชาชน", dataIndex: "peopleID", key: "peopleID" },
      { title: "ชื่อจริง", dataIndex: "realName", key: "realName" },
      { title: "ธนาคาร", dataIndex: "payBank", key: "payBank" },
      { title: "Pending Amount", dataIndex: "payout", key: "payout" },
    ];

    const matchingColumns = [
      ...baseColumns.filter((c) => c.dataIndex !== "payout"),
      { title: "Amount", dataIndex: "payout", key: "payout" },
    ];

    const mismatchedColumns = [
      { title: "Vendor ID", dataIndex: "htiVendorId", key: "htiVendorId" },
      {
        title: "Vendor Name",
        dataIndex: "htiVendorName",
        key: "htiVendorName",
      },
      { title: "เลขบัตรประชาชน", dataIndex: "peopleID", key: "peopleID" },
      { title: "ชื่อจริง", dataIndex: "realName", key: "realName" },
      { title: "ธนาคาร", dataIndex: "payBank", key: "payBank" },
      { title: "CSV Amount", dataIndex: "csvAmount", key: "csvAmount" },
      { title: "Pending Amount", dataIndex: "payout", key: "payout" },
      { title: "หมายเหตุ", dataIndex: "note", key: "note" },
    ];

    const partialColumns = [
      ...mismatchedColumns.filter((c) => c.dataIndex !== "note"),
      { title: "เดือนที่จ่ายแล้ว", dataIndex: "paidMonths", key: "paidMonths" },
      {
        title: "ยอดคงค้างรอบหน้า",
        dataIndex: "remainingPayout",
        key: "remainingPayout",
      },
    ];

    const pendingNoMatchColumns = [
      { title: "Vendor ID", dataIndex: "htiVendorId", key: "htiVendorId" },
      {
        title: "Vendor Name",
        dataIndex: "htiVendorName",
        key: "htiVendorName",
      },
      { title: "เลขบัตรประชาชน", dataIndex: "peopleID", key: "peopleID" },
      { title: "ชื่อจริง", dataIndex: "realName", key: "realName" },
      { title: "ธนาคาร", dataIndex: "payBank", key: "payBank" },
      { title: "Pending Amount", dataIndex: "payout", key: "payout" },
    ];

    const csvNoMatchColumns = [
      { title: "Vendor ID", dataIndex: "vendorId", key: "vendorId" },
      { title: "Vendor Name", dataIndex: "vendorName", key: "vendorName" },
      { title: "CSV Amount", dataIndex: "amount", key: "amount" },
    ];

    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Title level={3}>ตรวจสอบข้อมูล</Title>
        <Button onClick={() => setStage(STAGE.UPLOAD)}>
          กลับไปหน้าอัพโหลด
        </Button>

        <Title level={4} type="success">
          รายการที่ตรงกัน ({matchingPayments.length})
        </Title>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              matchingPayments,
              matchingColumns,
              "matching_payments.csv"
            )
          }
          disabled={matchingPayments.length === 0}
        >
          Download as CSV
        </Button>
        <Popconfirm
          title="ยืนยันการอัพเดตสถานะ"
          description={`คุณต้องการอัพเดตสถานะ ${matchingPayments.length} รายการที่ตรงกันเป็นสำเร็จ หรือไม่?`}
          onConfirm={confirmPaymentUpdate}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
        >
          <Button
            type="primary"
            loading={loading}
            disabled={matchingPayments.length === 0}
          >
            ยืนยันการอัพเดต ({matchingPayments.length} รายการ)
          </Button>
        </Popconfirm>
        <Table
          dataSource={matchingPayments}
          columns={matchingColumns}
          rowKey="htiVendorId"
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 5 }}
        />

        <Title level={4} type="success">
          รายการที่ยอดตรงกับบางเดือน ({partialPayments.length})
        </Title>
        <Paragraph>
          ยอดรอจ่ายมีรายการค้างเก่าปนอยู่ แต่พบชุดรายการเพียงชุดเดียวที่ยอดจ่าย
          60% รวมกันเท่ากับยอดใน CSV จะอัพเดตเฉพาะรายการชุดนั้นเป็นสำเร็จ
          รายการที่เหลือยังรอจ่ายในรอบหน้า
        </Paragraph>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              partialPayments,
              partialColumns,
              "partial_matching_payments.csv"
            )
          }
          disabled={partialPayments.length === 0}
        >
          Download as CSV
        </Button>
        <Popconfirm
          title="ยืนยันการอัพเดตสถานะ"
          description={`คุณต้องการอัพเดตเฉพาะเดือนที่ยอดตรงของ ${partialPayments.length} รายการเป็นสำเร็จ หรือไม่?`}
          onConfirm={confirmPartialUpdate}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
        >
          <Button
            type="primary"
            loading={loading}
            disabled={partialPayments.length === 0}
          >
            ยืนยันการอัพเดตเฉพาะเดือนที่ตรง ({partialPayments.length} รายการ)
          </Button>
        </Popconfirm>
        <Table
          dataSource={partialPayments}
          columns={partialColumns}
          rowKey="htiVendorId"
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 5 }}
        />

        <Title level={4} type="warning">
          รายการที่ยอดเงินไม่ตรงกัน ({mismatchedPayments.length})
        </Title>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              mismatchedPayments,
              mismatchedColumns,
              "mismatched_payments.csv"
            )
          }
          disabled={mismatchedPayments.length === 0}
        >
          Download as CSV
        </Button>
        <Popconfirm
          title="ยืนยันการพักการจ่าย"
          description={`คุณต้องการปรับสถานะ ${mismatchedPayments.length} รายการที่ยอดเงินไม่ตรงกันเป็น "การจ่ายไม่สำเร็จ" หรือไม่?`}
          onConfirm={confirmMismatchedUpdate}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
        >
          <Button
            type="primary"
            danger
            loading={loading}
            disabled={mismatchedPayments.length === 0}
          >
            ปรับสถานะรายการยอดเงินไม่ตรงกันเป็น "การจ่ายไม่สำเร็จ"
          </Button>
        </Popconfirm>
        <Table
          dataSource={mismatchedPayments}
          columns={mismatchedColumns}
          rowKey="htiVendorId"
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 5 }}
        />

        <Title level={4} type="danger">
          รายการรอจ่ายที่ไม่มีในไฟล์ CSV ({pendingWithoutMatch.length})
        </Title>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              pendingWithoutMatch,
              pendingNoMatchColumns,
              "pending_without_match.csv"
            )
          }
          disabled={pendingWithoutMatch.length === 0}
        >
          Download as CSV
        </Button>
        <Popconfirm
          title="ยืนยันการพักการจ่าย"
          description={`คุณต้องการปรับสถานะ ${pendingWithoutMatch.length} รายการที่ไม่มีในไฟล์ CSV เป็น "การจ่ายไม่สำเร็จ" หรือไม่?`}
          onConfirm={confirmPendingNoMatchUpdate}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
        >
          <Button
            type="primary"
            danger
            loading={loading}
            disabled={pendingWithoutMatch.length === 0}
          >
            ปรับสถานะรายการที่ไม่มีในไฟล์เป็น "การจ่ายไม่สำเร็จ"
          </Button>
        </Popconfirm>
        <Table
          dataSource={pendingWithoutMatch}
          columns={pendingNoMatchColumns}
          rowKey="htiVendorId"
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 5 }}
        />

        <Title level={4} type="danger">
          รายการใน CSV ที่ไม่มีในระบบ ({csvWithoutMatch.length})
        </Title>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              csvWithoutMatch,
              csvNoMatchColumns,
              "csv_without_match.csv"
            )
          }
          disabled={csvWithoutMatch.length === 0}
        >
          Download as CSV
        </Button>
        <Table
          dataSource={csvWithoutMatch}
          columns={csvNoMatchColumns}
          rowKey="vendorId"
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 5 }}
        />
      </Space>
    );
  };

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}
      {stage === STAGE.UPLOAD && (
        <>
          <Title level={3}>อัพโหลดไฟล์ CSV สำหรับทำจ่าย</Title>
          <Paragraph>
            ไฟล์ CSV ควรมี 3 columns: Vendor ID, Vendor Name, Amount
          </Paragraph>
          <Paragraph>
            เลือกเดือนรายได้ล่าสุดของรอบทำจ่ายนี้ (เช่น รอบจ่าย 20/10 =
            รายได้ถึงเดือน 09) รายได้เดือนหลังจากนั้นเป็นของรอบถัดไป
            จะไม่นำมาเทียบกับ CSV
          </Paragraph>
          <DatePicker
            value={lastIncomeMonth}
            onChange={setLastIncomeMonth}
            picker="month"
          />
          <Upload
            beforeUpload={fileChanged}
            accept=".csv"
            showUploadList={false}
            disabled={!lastIncomeMonth}
          >
            <Button
              icon={<UploadOutlined />}
              loading={loading}
              disabled={!lastIncomeMonth}
            >
              Click to Upload .csv
            </Button>
          </Upload>
        </>
      )}
      {stage === STAGE.CHECK && <CheckStage />}
    </Space>
  );
}

export default PaymentUpdate;
